import classnames from 'classnames';
import React, { Children, cloneElement, useEffect } from 'react';
import useIsMobile from 'tradera/hooks/use-is-mobile';

import { useCarouselContext } from './carousel';
import { ContentItem } from './carousel-content-item';

import css from './carousel.module.scss';
import { omitBy } from 'lodash';

type CarouselContentProps = {
    children?: React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
};

function isReactElement(child: React.ReactNode): child is React.ReactElement {
    if (child === null) return true;
    if (typeof child === 'string' || typeof child === 'number') return false;

    if (Array.isArray(child)) return child.every(isReactElement);

    if (typeof child === 'object') return 'props' in child;

    return false;
}

/**
 * The component that holds all scrollable items.
 * This one will be wider than the Carousel wrapper and have overflow:hidden
 */
export const CarouselContent = ({
    children,
    className,
    style,
    ...props
}: CarouselContentProps) => {
    const context = useCarouselContext();

    const {
        numberItemsToShow,
        gap,
        uniqueId,
        contentWrapperRef,
        showScrollbar,
        isFirstItemVisible,
        isLastItemVisible,
        rows,
        padX
    } = context;

    const childrenArray = Children.toArray(children);

    const enhancedChildren = isReactElement(children)
        ? childrenArray.map((child, index: number) => {
              return (
                  (child as React.ReactElement) !== null && (
                      <ContentItem
                          index={index}
                          key={(child as React.ReactElement).key}>
                          {({ isInView }) => {
                              return cloneElement(child as React.ReactElement, {
                                  // Set tabIndex=-1 when not in view to allow for the keyboard-navigating
                                  // user to not have to scroll through all items in the carousel
                                  tabIndex: isInView ? 0 : -1
                              });
                          }}
                      </ContentItem>
                  )
              );
          })
        : null;

    useEffect(() => {
        setTimeout(() => {
            // reset the scroll position to the top left of the document.
            // if we do not do this the scroll position will be "random" on initial render
            if (contentWrapperRef.current?.scrollLeft) {
                contentWrapperRef.current.scroll(0, 0);
            }
        }, 0);
    }, [contentWrapperRef]);

    const isMobile = useIsMobile();
    const paddingX = padX && isMobile ? gap : undefined;

    return (
        <div
            ref={contentWrapperRef}
            id={uniqueId?.current}
            className={classnames(className, css.contentWrapper, {
                [css.showScrollbar]:
                    showScrollbar && !(isFirstItemVisible && isLastItemVisible),
                [css.fixed]: (numberItemsToShow ?? 0) > 0 && rows > 1,
                [css.fluid]: typeof numberItemsToShow === 'undefined',
                [css.rows2]: rows === 2,
                [css.rows3]: rows === 3,
                [css.rows4]: rows === 4
            })}
            style={omitBy(
                {
                    ...style,
                    gap: gap ? `${gap}` : '',
                    paddingLeft: paddingX,
                    paddingRight: paddingX,
                    scrollPadding: paddingX
                },
                (value) => !value
            )}
            aria-live="polite"
            {...props}>
            {enhancedChildren}
        </div>
    );
};
